<script setup lang="ts">
import { PageHeader, PageShell } from '@taler/ui'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import {
  canManageReceiptScans,
  ReceiptScanList,
  ReceiptUploadForm,
  useReceiptScanJobs,
} from '@/features/receipt-scans'
import { useSession } from '@/app/session'

const { t } = useI18n()
const session = useSession()
const router = useRouter()

const canView = computed(() => canManageReceiptScans(session.can))
const ledgerId = () => session.activeLedger.value?.id

const jobs = useReceiptScanJobs({ ledgerId, enabled: () => canView.value })

async function onUpload(input: { accountId: string; file: File }): Promise<void> {
  const created = await jobs.upload(input).catch(() => undefined)
  if (created !== undefined) await router.push({ name: 'receipt-scan-detail', params: { jobId: created.id } })
}

function onDelete(jobId: string): void {
  jobs.deleteJob(jobId).catch(() => undefined)
}
</script>

<template>
  <PageShell class="gap-6">
    <PageHeader :title="t('receiptScan.title')">
      <template #subtitle>{{ t('receiptScan.description') }}</template>
    </PageHeader>

    <p v-if="session.isLoading.value" role="status">{{ t('app.loading') }}</p>

    <p v-else-if="session.activeLedger.value === undefined" class="text-[color:var(--color-text-muted)]">
      {{ t('receiptScan.noLedger') }}
    </p>

    <p v-else-if="!canView" class="text-[color:var(--color-text-muted)]">
      {{ t('receiptScan.noAccess') }}
    </p>

    <template v-else>
      <ReceiptUploadForm
        :is-uploading="jobs.isUploading.value"
        :error-detail="jobs.uploadErrorDetail.value"
        @submit="onUpload"
      />

      <ReceiptScanList
        :jobs="jobs.jobs.value"
        :is-loading="jobs.isLoading.value"
        :error="jobs.error.value"
        :retrying="jobs.retrying.value"
        :is-deleting="jobs.isDeleting.value"
        @retry="jobs.retry()"
        @delete="onDelete"
      />
    </template>
  </PageShell>
</template>
